import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CallRecordsService } from '../../../core/services/call-records/call-records.service';
import { Device, Call } from '@twilio/voice-sdk';

type SoftphoneState = 'idle' | 'dialing' | 'ringing' | 'in-call' | 'on-hold' | 'post-call' | 'error';

@Component({
  selector: 'app-softphone-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './softphone-widget.component.html',
  styleUrl: './softphone-widget.component.css',
})
export class SoftphoneWidgetComponent implements OnInit, AfterViewInit, OnDestroy {
  isExpanded = false;
  isMinimized = false;
  state: SoftphoneState = 'idle';
  phoneNumber = '';
  isMuted = false;
  callDuration = 0;
  contactName = '';
  errorMessage = '';
  voipConfigured = true;
  lastCallSid = '';
  lastCallReference = '';

  private device: Device | null = null;
  private activeCall: Call | null = null;
  private callTimer: any = null;
  private tokenRefreshTimer: any = null;

  // Drag state
  posX = -1;
  posY = -1;
  private isDragging = false;
  private wasDragged = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private boundMouseMove: any;
  private boundMouseUp: any;
  private boundTouchMove: any;
  private boundTouchEnd: any;

  @ViewChild('draggableEl') draggableEl!: ElementRef<HTMLElement>;

  dialpadKeys = [
    { digit: '1', letters: '' }, { digit: '2', letters: 'ABC' }, { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' }, { digit: '5', letters: 'JKL' }, { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' }, { digit: '8', letters: 'TUV' }, { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' }, { digit: '0', letters: '+' }, { digit: '#', letters: '' },
  ];

  constructor(private callRecordsService: CallRecordsService) {}

  ngOnInit(): void {
    this.initDevice();
  }

  ngAfterViewInit(): void {
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
    this.boundTouchMove = this.onTouchMove.bind(this);
    this.boundTouchEnd = this.onTouchEnd.bind(this);
    this.loadSavedPosition();
  }

  @HostListener('window:softphone-call', ['$event'])
  onSoftphoneCallEvent(event: CustomEvent): void {
    console.log('[Softphone] Received softphone-call event:', event.detail);
    const { phone, name, order_reference, client_reference, silent } = event.detail;
    if (phone) {
      this.callNumber(phone, name, order_reference, client_reference, silent);
    }
  }

  @HostListener('window:softphone-hangup')
  onSoftphoneHangupEvent(): void {
    this.hangup();
  }

  ngOnDestroy(): void {
    this.cleanupDragListeners();
    if (this.callTimer) clearInterval(this.callTimer);
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);
    this.device?.destroy();
  }

  // === Twilio Device ===

  private initDevice(): void {
    console.log('[Softphone] Initializing device — requesting voice token...');
    this.callRecordsService.getVoiceToken().subscribe({
      next: (res) => {
        const { token, identity, expires_in } = res.data;
        console.log('[Softphone] Token received — identity:', identity, '— expires_in:', expires_in, 's');
        this.device = new Device(token);
        this.voipConfigured = true;
        console.log('[Softphone] Device created successfully');
        this.scheduleTokenRefresh(expires_in);

        this.device.on('tokenWillExpire', () => {
          console.log('[Softphone] Token about to expire — refreshing...');
          this.callRecordsService.getVoiceToken().subscribe({
            next: (r) => {
              this.device?.updateToken(r.data.token);
              console.log('[Softphone] Token refreshed via tokenWillExpire event');
            },
          });
        });
      },
      error: (err) => {
        console.error('[Softphone] Failed to get voice token:', err?.status, err?.error);
        const msg = err?.error?.errors?.account_voips?.[0]?.msg;
        if (msg === 'swal_messages.account_voips.voip_not_configured') {
          console.warn('[Softphone] VoIP not configured for this user/account');
          this.voipConfigured = false;
        }
      },
    });
  }

  private scheduleTokenRefresh(expiresIn: number): void {
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);
    const refreshMs = Math.max((expiresIn - 60), 30) * 1000;
    console.log('[Softphone] Token refresh scheduled in', Math.round(refreshMs / 1000), 's');
    this.tokenRefreshTimer = setTimeout(() => {
      console.log('[Softphone] Refreshing token (scheduled)...');
      this.callRecordsService.getVoiceToken().subscribe({
        next: (res) => {
          this.device?.updateToken(res.data.token);
          console.log('[Softphone] Token refreshed (scheduled) — next in', res.data.expires_in, 's');
          this.scheduleTokenRefresh(res.data.expires_in);
        },
      });
    }, refreshMs);
  }

  // === Call logic ===

  callNumber(number: string, name?: string, orderRef?: string, clientRef?: string, silent?: boolean): void {
    console.log('[Softphone] callNumber() →', number, '— name:', name, '— orderRef:', orderRef, '— clientRef:', clientRef, '— silent:', silent);
    this.phoneNumber = number;
    this.contactName = name || '';
    if (!silent) {
      this.isExpanded = true;
    } else {
      this.isMinimized = true;
    }
    this.call(orderRef, clientRef);
  }

  call(orderReference?: string, clientReference?: string): void {
    if (!this.phoneNumber || !this.device) {
      console.warn('[Softphone] call() aborted — phoneNumber:', this.phoneNumber, '— device:', !!this.device);
      return;
    }
    if (!this.voipConfigured) {
      console.warn('[Softphone] call() aborted — VoIP not configured');
      this.errorMessage = 'communication.voip.not_configured';
      this.state = 'error';
      return;
    }

    this.state = 'dialing';
    this.contactName = this.contactName || '';
    this.errorMessage = '';
    this.emitStateChange('dialing');

    const params: any = { To: this.phoneNumber };
    if (orderReference) params.order_reference = orderReference;
    if (clientReference) params.client_reference = clientReference;

    console.log('[Softphone] device.connect() — params:', JSON.stringify(params));

    this.device.connect({ params }).then((call: Call) => {
      this.activeCall = call;
      this.lastCallSid = (call as any).parameters?.CallSid || '';
      console.log('[Softphone] device.connect() resolved — CallSid:', this.lastCallSid);

      call.on('ringing', () => {
        console.log('[Softphone] Event: ringing');
        this.state = 'ringing';
        this.emitStateChange('ringing');
      });

      call.on('accept', () => {
        console.log('[Softphone] Event: accept — call connected, starting timer');
        this.state = 'in-call';
        this.callDuration = 0;
        this.callTimer = setInterval(() => {
          this.callDuration++;
          this.emitStateChange('in-call', this.callDuration);
        }, 1000);
        this.emitStateChange('in-call', 0);
      });

      call.on('disconnect', () => {
        console.log('[Softphone] Event: disconnect — call ended');
        this.emitStateChange('ended', this.callDuration);
        this.onCallEnded();
      });

      call.on('cancel', () => {
        console.log('[Softphone] Event: cancel — call cancelled');
        this.emitStateChange('ended', this.callDuration);
        this.onCallEnded();
      });

      call.on('error', (error: any) => {
        console.error('[Softphone] Event: error —', error?.code, error?.message, error);
        this.errorMessage = 'communication.voip.call_failed';
        this.state = 'error';
        if (this.callTimer) { clearInterval(this.callTimer); this.callTimer = null; }
        this.emitStateChange('error');
      });
    }).catch((err: any) => {
      console.error('[Softphone] device.connect() rejected:', err);
      this.errorMessage = 'communication.voip.call_failed';
      this.state = 'error';
    });
  }

  private onCallEnded(): void {
    console.log('[Softphone] onCallEnded() — duration:', this.callDuration, 's — CallSid:', this.lastCallSid);
    if (this.callTimer) { clearInterval(this.callTimer); this.callTimer = null; }
    this.activeCall = null;
    this.state = 'post-call';
    this.isMuted = false;

    // Resolve call reference from call_sid for post-call editing
    if (this.lastCallSid) {
      console.log('[Softphone] Resolving call reference from call_sid:', this.lastCallSid);
      this.callRecordsService.getCalls({ call_sid: this.lastCallSid }).subscribe({
        next: (res) => {
          if (res.data?.length > 0) {
            this.lastCallReference = res.data[0].reference;
            console.log('[Softphone] Call reference resolved:', this.lastCallReference);
          } else {
            console.warn('[Softphone] No call found for call_sid:', this.lastCallSid);
          }
        },
        error: (err) => {
          console.error('[Softphone] Error resolving call reference:', err?.status, err?.error);
        },
      });
    }
  }

  hangup(): void {
    console.log('[Softphone] hangup() — activeCall:', !!this.activeCall);
    this.activeCall?.disconnect();
    if (!this.activeCall) {
      if (this.callTimer) { clearInterval(this.callTimer); this.callTimer = null; }
      this.state = 'post-call';
      this.isMuted = false;
    }
  }

  endPostCall(): void {
    this.state = 'idle';
    this.phoneNumber = '';
    this.callDuration = 0;
    this.contactName = '';
    this.lastCallSid = '';
    this.lastCallReference = '';
    this.errorMessage = '';
  }

  toggleMute(): void {
    if (this.activeCall) {
      this.isMuted = !this.isMuted;
      this.activeCall.mute(this.isMuted);
      console.log('[Softphone] toggleMute() →', this.isMuted ? 'MUTED' : 'UNMUTED');
    }
  }

  toggleHold(): void {
    this.state = this.state === 'on-hold' ? 'in-call' : 'on-hold';
  }

  // === Drag & Drop ===

  onDragStart(e: MouseEvent): void {
    e.preventDefault();
    this.startDrag(e.clientX, e.clientY);
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  onTouchStart(e: TouchEvent): void {
    const touch = e.touches[0];
    this.startDrag(touch.clientX, touch.clientY);
    document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    document.addEventListener('touchend', this.boundTouchEnd);
  }

  private startDrag(clientX: number, clientY: number): void {
    this.isDragging = true;
    this.wasDragged = false;
    this.dragStartX = clientX;
    this.dragStartY = clientY;
    const el = this.draggableEl?.nativeElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      this.dragOffsetX = clientX - rect.left;
      this.dragOffsetY = clientY - rect.top;
    }
  }

  private onMouseMove(e: MouseEvent): void { this.moveDrag(e.clientX, e.clientY); }
  private onTouchMove(e: TouchEvent): void { e.preventDefault(); this.moveDrag(e.touches[0].clientX, e.touches[0].clientY); }

  private moveDrag(clientX: number, clientY: number): void {
    if (!this.isDragging) return;
    if (Math.abs(clientX - this.dragStartX) > 5 || Math.abs(clientY - this.dragStartY) > 5) this.wasDragged = true;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const el = this.draggableEl?.nativeElement;
    const w = el ? el.offsetWidth : 52;
    const h = el ? el.offsetHeight : 52;
    this.posX = Math.max(0, Math.min(clientX - this.dragOffsetX, vw - w));
    this.posY = Math.max(0, Math.min(clientY - this.dragOffsetY, vh - h));
  }

  private onMouseUp(): void { this.endDrag(); document.removeEventListener('mousemove', this.boundMouseMove); document.removeEventListener('mouseup', this.boundMouseUp); }
  private onTouchEnd(): void { this.endDrag(); document.removeEventListener('touchmove', this.boundTouchMove); document.removeEventListener('touchend', this.boundTouchEnd); }

  private endDrag(): void {
    this.isDragging = false;
    this.saveDragPosition();
  }

  private saveDragPosition(): void {
    if (this.posX >= 0 && this.posY >= 0) {
      try { localStorage.setItem('softphone_pos', JSON.stringify({ x: this.posX, y: this.posY })); } catch {}
    }
  }

  private cleanupDragListeners(): void {
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
    document.removeEventListener('touchmove', this.boundTouchMove);
    document.removeEventListener('touchend', this.boundTouchEnd);
  }

  getDragStyle(): any {
    if (this.posX < 0 || this.posY < 0) return {};
    return { left: this.posX + 'px', top: this.posY + 'px', right: 'auto', bottom: 'auto' };
  }

  loadSavedPosition(): void {
    try {
      const saved = localStorage.getItem('softphone_pos');
      if (saved) { const pos = JSON.parse(saved); this.posX = pos.x; this.posY = pos.y; }
    } catch {}
  }

  // === UI helpers ===

  toggle(): void {
    if (this.wasDragged) { this.wasDragged = false; return; }
    if (this.state !== 'idle' && this.state !== 'post-call' && this.state !== 'error') {
      this.isMinimized = !this.isMinimized;
    } else {
      this.isExpanded = !this.isExpanded;
    }
  }

  pressKey(digit: string): void { this.phoneNumber += digit; }
  backspace(): void { this.phoneNumber = this.phoneNumber.slice(0, -1); }

  formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  getStatusColor(): string {
    if (!this.voipConfigured) return '#aaa';
    const map: Record<string, string> = { idle: '#22c55e', dialing: '#f1b44c', ringing: '#f1b44c', 'in-call': '#990000', 'on-hold': '#f1b44c', 'post-call': '#22c55e', error: '#dc3545' };
    return map[this.state] || '#22c55e';
  }

  getStateLabel(): string {
    const map: Record<string, string> = {
      idle: '', dialing: 'communication.voip.dialing',
      ringing: 'communication.voip.ringing', 'in-call': 'communication.voip.in_call',
      'on-hold': 'communication.voip.on_hold', 'post-call': 'communication.voip.call_ended',
      error: 'communication.voip.call_failed',
    };
    return map[this.state] || '';
  }

  private emitStateChange(state: string, duration?: number): void {
    window.dispatchEvent(new CustomEvent('softphone-state-change', {
      detail: { state, duration: duration ?? 0, phone: this.phoneNumber, call_sid: this.lastCallSid },
    }));
  }
}
